import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Wallet, Eye, EyeOff, Sparkles, CheckCircle2, MessageCircle, Star, ArrowRight } from "lucide-react";
import api, { useAuthApply } from "@/lib/auth-helpers";
import { apiError } from "@/lib/api";
import { toast } from "sonner";
import { ContactPopup } from "@/components/ContactPopup";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const fadeUp = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
  transition: { duration: 0.25, ease: "easeOut" },
};

const AVATARS = [
  "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&auto=format&fit=crop&q=80",
  "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&auto=format&fit=crop&q=80",
  "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&auto=format&fit=crop&q=80",
  "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&auto=format&fit=crop&q=80",
];

const FLOATING_CARDS = [
  {
    icon: CheckCircle2, iconCls: "bg-emerald-50 text-emerald-500",
    title: "SPP Lunas Rp 150.000", subtitle: "Siti Aminah · Kelas 5A", status: "Otomatis terverifikasi",
    cls: "-rotate-3 lg:absolute lg:left-0 lg:top-2 lg:z-20",
  },
  {
    icon: MessageCircle, iconCls: "bg-[#E7F8EE] text-[#25D366]",
    title: "Notifikasi WA Orang Tua", subtitle: "Kwitansi SPP terkirim ke wali murid", status: "Terkirim · 10.42 WIB",
    cls: "rotate-3 lg:absolute lg:right-0 lg:top-40 lg:z-30",
  },
  {
    icon: Wallet, iconCls: "bg-[#E5F0FA] text-[#0060AF]",
    title: "Ringkasan Kas Hari Ini", subtitle: "Pemasukan Rp 4.250.000 · 28 transaksi", status: "Saldo realtime Rp 48.120.000",
    cls: "-rotate-2 lg:absolute lg:bottom-4 lg:left-8 lg:z-10",
  },
];

const PRIMARY_BTN = "h-12 w-full rounded-full bg-gradient-to-r from-[#0060AF] to-[#00A6E0] text-base font-semibold text-white shadow-[0_10px_25px_rgba(0,96,175,0.25)] transition-all duration-150 hover:opacity-95 hover:shadow-[0_12px_30px_rgba(0,96,175,0.35)] hover:text-white active:scale-95";

function PlanCard({ plan, selected, onSelect }) {
  const popular = plan.id === "3bulan";
  return (
    <motion.button
      type="button"
      whileHover={{ y: -2 }}
      whileTap={{ scale: 0.97 }}
      onClick={() => onSelect(plan.id)}
      data-testid={`plan-${plan.id}`}
      className={`relative rounded-xl border-2 p-4 text-left transition-[border-color,background-color,box-shadow] duration-150 ${
        selected
          ? "border-[#0060AF] bg-[#E5F0FA] shadow-[0_8px_30px_rgba(0,96,175,0.14)]"
          : "border-[#DBE9F6] bg-white hover:border-[#7FB8E4] hover:shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
      }`}
    >
      {popular && (
        <span className="absolute -top-2.5 left-3 rounded-full bg-gradient-to-r from-[#0060AF] to-[#00A6E0] px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
          Sehari Seribu
        </span>
      )}
      <p className="whitespace-nowrap font-heading text-base font-bold text-[#0F172A] sm:text-lg">
        Rp {plan.price.toLocaleString("id-ID")}
      </p>
      <p className="mt-1 flex items-start gap-1 text-[11px] leading-snug text-[#475569]">
        {selected && <CheckCircle2 className="mt-0.5 h-3 w-3 shrink-0 text-[#0060AF]" strokeWidth={2} />}
        {plan.label}
        {plan.months > 1 ? ` · ${plan.months} bulan` : " · per bulan"}
      </p>
    </motion.button>
  );
}

export default function Login() {
  const applyAuth = useAuthApply();
  const navigate = useNavigate();
  const [tab, setTab] = useState("masuk");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [demoLoading, setDemoLoading] = useState("");
  const [demoError, setDemoError] = useState("");
  const [plans, setPlans] = useState([]);
  const [reg, setReg] = useState({ school_name: "", name: "", email: "", password: "", phone: "", plan: "3bulan" });
  const [regError, setRegError] = useState("");
  const [regLoading, setRegLoading] = useState(false);
  const [regDone, setRegDone] = useState(null);
  const [adminContact, setAdminContact] = useState(null);
  const [platform, setPlatform] = useState(null);
  const [regProof, setRegProof] = useState(null);
  const [proofSent, setProofSent] = useState(false);
  const [uploadingProof, setUploadingProof] = useState(false);

  useEffect(() => {
    api.get("/public/admin-contact").then((r) => setAdminContact(r.data)).catch(() => {});
    api.get("/public/platform").then((r) => setPlatform(r.data)).catch(() => {});
    api.get("/auth/plans").then((r) => setPlans(r.data)).catch(() => {});
  }, []);

  const handleProof = async (e) => {
    e.preventDefault();
    if (!regProof) return;
    setUploadingProof(true);
    try {
      const fd = new FormData();
      fd.append("email", reg.email);
      fd.append("proof", regProof);
      await api.post("/public/subscription-proof", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setProofSent(true);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setUploadingProof(false);
    }
  };

  const goHome = (user) => navigate(user.role === "orang_tua" ? "/anak" : "/", { replace: true });

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      applyAuth(data.token, data.user);
      goHome(data.user);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Terjadi kesalahan. Coba lagi.");
    } finally {
      setLoading(false);
    }
  };

  const handleDemo = async (role) => {
    setDemoLoading(role);
    setDemoError("");
    try {
      const { data } = await api.post(`/auth/demo?role=${role}`);
      applyAuth(data.token, data.user);
      goHome(data.user);
    } catch (err) {
      setDemoError(apiError(err));
      setDemoLoading("");
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setRegError("");
    setRegLoading(true);
    try {
      const { data } = await api.post("/auth/register", reg);
      setRegDone(data);
    } catch (err) {
      setRegError(apiError(err));
    } finally {
      setRegLoading(false);
    }
  };

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  const navTo = (t) => { setTab(t); scrollTo("auth-card"); };

  const inputCls = "h-12 rounded-lg border-[#DBE9F6] bg-[#F4F9FE] text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:ring-2 focus-visible:ring-[#0060AF]";

  return (
    <div className="min-h-screen bg-[#F4F9FE]">
      <div className="pointer-events-none absolute left-1/2 top-0 -z-0 h-[480px] w-[820px] -translate-x-1/2 rounded-full bg-gradient-to-tr from-[#0060AF]/12 via-[#00A6E0]/10 to-[#7FB8E4]/10 blur-3xl" />

      <header className="sticky top-0 z-40 w-full border-b border-[#DBE9F6] bg-[#F4F9FE]/85 px-4 py-3.5 backdrop-blur-md sm:px-8">
        <div className="mx-auto flex max-w-7xl items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-[#0060AF] to-[#00A6E0] shadow-[0_6px_18px_rgba(0,96,175,0.3)]">
              <Wallet className="h-5 w-5 text-white" strokeWidth={1.5} />
            </div>
            <div>
              <p className="font-heading text-base font-bold leading-tight text-[#0F172A]">KasPintar</p>
              <p className="text-[11px] text-[#94A3B8]">by EduNexa</p>
            </div>
          </div>
          <nav className="flex items-center gap-2 sm:gap-5">
            <button type="button" onClick={() => scrollTo("tentang-kami")} data-testid="nav-about"
                    className="hidden text-sm font-medium text-[#475569] transition-colors duration-150 hover:text-[#0F172A] sm:block">
              Tentang Kami
            </button>
            <button type="button" onClick={() => navTo("demo")} data-testid="nav-demo"
                    className="hidden text-sm font-medium text-[#475569] transition-colors duration-150 hover:text-[#0F172A] sm:block">
              Akun Demo
            </button>
            <button type="button" onClick={() => navTo("masuk")} data-testid="nav-login-cta"
                    className="flex items-center gap-1.5 rounded-full bg-gradient-to-r from-[#0060AF] to-[#00A6E0] px-4 py-2 text-sm font-semibold text-white shadow-[0_8px_20px_rgba(0,96,175,0.25)] transition-transform duration-150 hover:scale-[1.03] active:scale-95">
              Masuk ke Sistem <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
            </button>
          </nav>
        </div>
      </header>

      <main className="relative mx-auto grid max-w-7xl grid-cols-1 items-start gap-12 px-4 py-10 sm:px-8 lg:grid-cols-12 lg:gap-16 lg:py-16">
        <div className="col-span-1 space-y-6 lg:col-span-7">
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: "easeOut" }}
                      className="inline-flex items-center gap-2 rounded-full border border-[#BBD9F2] bg-[#E5F0FA] px-3.5 py-1.5 text-xs font-semibold text-[#0060AF]">
            <Sparkles className="h-3.5 w-3.5" strokeWidth={1.5} /> Platform keuangan sekolah serba otomatis
          </motion.div>

          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08, duration: 0.45, ease: "easeOut" }}
                     className="font-heading text-4xl font-extrabold leading-[1.15] tracking-tight text-[#0F172A] sm:text-5xl lg:text-6xl">
            Kelola SPP & kas sekolah jadi sangat{" "}
            <span className="bg-gradient-to-r from-[#0060AF] via-[#0E7CD6] to-[#00A6E0] bg-clip-text text-transparent drop-shadow-sm">rapi.</span>
          </motion.h1>

          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.16, duration: 0.45, ease: "easeOut" }}
                    className="max-w-xl text-base leading-relaxed text-[#475569]">
            Gantikan pencatatan manual & rekap Excel yang ribet. Otomatisasi tagihan SPP, tabungan siswa,
            kas sekolah, hingga notifikasi WhatsApp ke orang tua — semua dalam satu platform.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.24, duration: 0.45, ease: "easeOut" }}
                      className="flex items-center gap-3">
            <div className="flex -space-x-2.5">
              {AVATARS.map((a) => (
                <img key={a} src={a} alt="" className="h-8 w-8 rounded-full border-2 border-[#F4F9FE] object-cover" />
              ))}
            </div>
            <div>
              <div className="flex items-center gap-0.5 text-[#F59E0B]">
                {[...Array(5)].map((_, i) => <Star key={i} className="h-3.5 w-3.5 fill-current" strokeWidth={0} />)}
              </div>
              <p className="mt-0.5 text-xs text-[#94A3B8]">Dipakai bendahara, kepala sekolah & orang tua — data tiap sekolah terpisah dan aman.</p>
            </div>
          </motion.div>

          <motion.div id="auth-card"
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.45, ease: "easeOut" }}
            className="scroll-mt-24 rounded-2xl border border-[#DBE9F6] bg-white p-6 shadow-[0_16px_40px_rgba(0,96,175,0.08)] sm:p-8"
          >
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="grid w-full grid-cols-3 rounded-full bg-[#E5F0FA] p-1" data-testid="login-tabs">
                <TabsTrigger value="masuk" data-testid="tab-masuk" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm">Masuk</TabsTrigger>
                <TabsTrigger value="demo" data-testid="tab-demo" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm">Akun Demo</TabsTrigger>
                <TabsTrigger value="daftar" data-testid="tab-daftar" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm">Daftar</TabsTrigger>
              </TabsList>

              <AnimatePresence mode="wait">
                {tab === "masuk" && (
                  <TabsContent value="masuk" forceMount asChild key="masuk">
                    <motion.div {...fadeUp}>
                      <form onSubmit={handleLogin} className="mt-6 space-y-5" data-testid="login-form">
                        <div className="space-y-2">
                          <Label htmlFor="email" className="text-[#0F172A]">Email atau NISN</Label>
                          <Input id="email" type="text" data-testid="login-email-input" placeholder="nama@sekolah.sch.id atau NISN"
                                 value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="username" className={inputCls} />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="password" className="text-[#0F172A]">Kata Sandi</Label>
                          <div className="relative">
                            <Input id="password" type={showPw ? "text" : "password"} data-testid="login-password-input"
                                   placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)}
                                   required autoComplete="current-password" className={`${inputCls} pr-11`} />
                            <button type="button" onClick={() => setShowPw(!showPw)} data-testid="login-toggle-password"
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] transition-colors duration-150 hover:text-[#475569]">
                              {showPw ? <EyeOff className="h-4 w-4" strokeWidth={1.5} /> : <Eye className="h-4 w-4" strokeWidth={1.5} />}
                            </button>
                          </div>
                        </div>
                        {error && (
                          <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-600" data-testid="login-error-message">{error}</div>
                        )}
                        <Button type="submit" data-testid="login-submit-button" disabled={loading} className={PRIMARY_BTN}>
                          {loading ? "Memproses..." : "Masuk"}
                        </Button>
                        <p className="text-center text-xs text-[#94A3B8]">Staf sekolah masuk dengan email. Orang tua masuk dengan NISN anak.</p>
                      </form>
                    </motion.div>
                  </TabsContent>
                )}

                {tab === "demo" && (
                  <TabsContent value="demo" forceMount asChild key="demo">
                    <motion.div {...fadeUp}>
                      <div className="mt-6 space-y-4" data-testid="demo-section">
                        <div className="rounded-lg bg-[#E5F0FA] px-4 py-3 text-sm leading-relaxed text-[#475569]">
                          Coba semua fitur KasPintar dengan data contoh — tanpa perlu mendaftar.
                        </div>
                        <Button onClick={() => handleDemo("bendahara")} disabled={!!demoLoading}
                                data-testid="demo-bendahara-button" className={PRIMARY_BTN}>
                          <Sparkles className="h-4 w-4" strokeWidth={1.5} />
                          {demoLoading === "bendahara" ? "Menyiapkan demo..." : "Masuk sebagai Bendahara Demo"}
                        </Button>
                        <Button onClick={() => handleDemo("orang_tua")} disabled={!!demoLoading} variant="outline"
                                data-testid="demo-ortu-button"
                                className="h-12 w-full gap-2 rounded-full border-[#7FB8E4] text-base font-semibold text-[#0060AF] transition-all duration-150 hover:bg-[#E5F0FA] hover:text-[#0060AF] active:scale-95">
                          {demoLoading === "orang_tua" ? "Menyiapkan demo..." : "Masuk sebagai Orang Tua Demo"}
                        </Button>
                        {demoError && (
                          <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-600" data-testid="demo-error-message">{demoError}</div>
                        )}
                        <p className="text-center text-xs text-[#94A3B8]">Sekolah demo · 4 siswa contoh · SPP, tabungan & tagihan</p>
                      </div>
                    </motion.div>
                  </TabsContent>
                )}

                {tab === "daftar" && (
                  <TabsContent value="daftar" forceMount asChild key="daftar">
                    <motion.div {...fadeUp}>
                      {regDone ? (
                        <div className="mt-6 space-y-5 text-center" data-testid="register-success">
                          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50">
                            <CheckCircle2 className="h-7 w-7 text-emerald-500" strokeWidth={1.5} />
                          </div>
                          <div>
                            <h3 className="font-heading text-lg font-semibold text-[#0F172A]">Pendaftaran Berhasil</h3>
                            <p className="mt-2 text-sm leading-relaxed text-[#475569]" data-testid="register-success-message">{regDone.message}</p>
                            <p className="mt-2 text-sm text-[#475569]">Paket: <b>{regDone.plan_label}</b> — Rp {regDone.price.toLocaleString("id-ID")}</p>
                          </div>
                          <div className="rounded-lg bg-[#E5F0FA] px-4 py-3 text-left text-xs leading-relaxed text-[#475569]" data-testid="register-success-contact">
                            {adminContact?.phone
                              ? <>Hubungi admin untuk pembayaran: <b>{adminContact.name}</b> — WA <b>{adminContact.phone}</b>{adminContact.email ? ` · ${adminContact.email}` : ""}. Setelah dikonfirmasi, Anda bisa masuk dengan email & kata sandi yang didaftarkan.</>
                              : "Admin KasPintar akan menghubungi Anda via WhatsApp untuk konfirmasi pembayaran. Setelah dikonfirmasi, Anda bisa masuk dengan email & kata sandi yang didaftarkan."}
                          </div>
                          {(platform?.payment_info?.account_number || platform?.qris_path) && (
                            <div className="rounded-lg border border-[#DBE9F6] p-4 text-left" data-testid="register-payment-info">
                              <p className="text-sm font-semibold text-[#0F172A]">Cara Pembayaran Paket</p>
                              {platform.payment_info?.account_number && (
                                <p className="mt-1 text-sm text-[#475569]" data-testid="register-bank-info">
                                  {platform.payment_info.bank_name}: <b className="font-mono">{platform.payment_info.account_number}</b> a.n. {platform.payment_info.account_name}
                                </p>
                              )}
                              {platform?.qris_path && (
                                <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${platform.qris_path}`} alt="QRIS pembayaran langganan"
                                     className="mt-2 h-36 w-36 rounded-lg border border-[#DBE9F6] object-cover" data-testid="register-qris-image" />
                              )}
                            </div>
                          )}
                          {!proofSent ? (
                            <form onSubmit={handleProof} className="space-y-2 text-left" data-testid="register-proof-form">
                              <Label className="text-sm font-semibold text-[#0F172A]">Sudah bayar? Unggah bukti transfer/QRIS</Label>
                              <Input type="file" accept=".jpg,.jpeg,.png,.webp" onChange={(e) => setRegProof(e.target.files?.[0] || null)} data-testid="register-proof-input" />
                              <Button type="submit" disabled={uploadingProof || !regProof} data-testid="register-proof-submit"
                                      className="w-full rounded-full bg-emerald-600 text-white hover:bg-emerald-700">
                                {uploadingProof ? "Mengirim..." : "Kirim Bukti Pembayaran"}
                              </Button>
                            </form>
                          ) : (
                            <div className="space-y-2">
                              <div className="rounded-lg bg-emerald-50 px-3 py-2 text-left text-sm text-emerald-700" data-testid="register-proof-success">
                                Bukti terkirim — admin mendapat notifikasi WhatsApp dan akan segera mengaktifkan akun Anda.
                              </div>
                              {adminContact?.phone && (
                                <a href={`https://wa.me/${adminContact.phone.replace(/\D/g, "").replace(/^0/, "62")}?text=${encodeURIComponent("Halo Admin 👋\nSaya baru saja mendaftarkan sekolah dan mengirim bukti pembayaran langganan KasPintar.\nMohon bantu proses aktivasi akun kami. Terima kasih 🙏")}`} target="_blank" rel="noreferrer"
                                   data-testid="register-contact-admin-wa"
                                   className="flex h-11 w-full items-center justify-center gap-2 rounded-full bg-[#25D366] text-sm font-semibold text-white transition-transform duration-100 hover:bg-[#1fb857] active:scale-95">
                                  <MessageCircle className="h-4 w-4" strokeWidth={1.5} /> Hubungi Admin via WhatsApp
                                </a>
                              )}
                            </div>
                          )}
                          <Button variant="outline" onClick={() => { setRegDone(null); setTab("masuk"); }} data-testid="register-success-login"
                                  className="w-full rounded-full border-[#DBE9F6]">
                            Kembali ke Halaman Masuk
                          </Button>
                        </div>
                      ) : (
                      <form onSubmit={handleRegister} className="mt-6 space-y-4" data-testid="register-form">
                        <div className="space-y-2">
                          <Label htmlFor="reg-school" className="text-[#0F172A]">Nama Sekolah</Label>
                          <Input id="reg-school" data-testid="register-school-input" value={reg.school_name}
                                 onChange={(e) => setReg({ ...reg, school_name: e.target.value })} required placeholder="Contoh: SDN 2 Harapan Bangsa" className={inputCls} />
                        </div>
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                          <div className="space-y-2">
                            <Label htmlFor="reg-name" className="text-[#0F172A]">Nama Anda (Bendahara)</Label>
                            <Input id="reg-name" data-testid="register-name-input" value={reg.name}
                                   onChange={(e) => setReg({ ...reg, name: e.target.value })} required placeholder="Nama lengkap" className={inputCls} />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="reg-email" className="text-[#0F172A]">Email</Label>
                            <Input id="reg-email" type="email" data-testid="register-email-input" value={reg.email}
                                   onChange={(e) => setReg({ ...reg, email: e.target.value })} required placeholder="nama@email.com" className={inputCls} />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="reg-password" className="text-[#0F172A]">Kata Sandi</Label>
                          <Input id="reg-password" type="password" data-testid="register-password-input" value={reg.password}
                                 onChange={(e) => setReg({ ...reg, password: e.target.value })} required minLength={6} placeholder="Minimal 6 karakter" className={inputCls} />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="reg-phone" className="text-[#0F172A]">No WhatsApp (untuk konfirmasi pembayaran)</Label>
                          <Input id="reg-phone" data-testid="register-phone-input" value={reg.phone}
                                 onChange={(e) => setReg({ ...reg, phone: e.target.value })} placeholder="Contoh: 081234567890" className={inputCls} />
                        </div>

                        <div className="space-y-3">
                          <Label className="text-[#0F172A]">Pilih Paket Langganan</Label>
                          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3" data-testid="plan-options">
                            {plans.map((p) => (
                              <PlanCard key={p.id} plan={p} selected={reg.plan === p.id} onSelect={(id) => setReg({ ...reg, plan: id })} />
                            ))}
                          </div>
                        </div>

                        {regError && (
                          <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-600" data-testid="register-error-message">{regError}</div>
                        )}
                        <Button type="submit" data-testid="register-submit-button" disabled={regLoading} className={PRIMARY_BTN}>
                          {regLoading ? "Mendaftarkan..." : "Daftar & Mulai"}
                        </Button>
                        <p className="text-center text-xs text-[#94A3B8]">Setelah daftar, admin KasPintar menghubungi Anda via WA untuk pembayaran paket.</p>
                      </form>
                      )}
                    </motion.div>
                  </TabsContent>
                )}
              </AnimatePresence>
            </Tabs>
          </motion.div>
        </div>

        <div className="relative col-span-1 flex flex-col gap-4 lg:col-span-5 lg:block lg:min-h-[500px] lg:pt-6">
          <div className="absolute inset-0 -z-10 rounded-full bg-gradient-to-tr from-[#0060AF]/18 via-[#00A6E0]/12 to-[#7FB8E4]/10 blur-3xl" />
          {FLOATING_CARDS.map((c, i) => (
            <motion.div key={c.title}
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 + i * 0.12, duration: 0.45, ease: "easeOut" }}
              className={`w-full max-w-[300px] rounded-2xl border border-[#DBE9F6] bg-white p-4 shadow-[0_20px_40px_rgba(15,42,67,0.07)] transition-transform duration-300 hover:rotate-0 ${c.cls}`}
              data-testid={`hero-card-${i}`}>
              <div className="flex items-start gap-3">
                <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${c.iconCls}`}>
                  <c.icon className="h-5 w-5" strokeWidth={1.5} />
                </div>
                <div className="min-w-0">
                  <p className="font-heading text-sm font-bold text-[#0F172A]">{c.title}</p>
                  <p className="mt-0.5 text-xs text-[#475569]">{c.subtitle}</p>
                  <p className="mt-1.5 text-[11px] font-medium text-[#94A3B8]">{c.status}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </main>

      {platform?.about_text && (
        <section id="tentang-kami" className="mx-auto mt-4 max-w-7xl scroll-mt-24 border-t border-[#DBE9F6] px-4 py-10 sm:px-8" data-testid="about-us-section">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.45, ease: "easeOut" }}
                      className="max-w-3xl">
            <h2 className="font-heading text-2xl font-bold tracking-tight text-[#0F172A] sm:text-3xl">{platform.about_title || "Tentang Kami"}</h2>
            <p className="mt-3 whitespace-pre-line text-base leading-relaxed text-[#475569]" data-testid="about-us-text">{platform.about_text}</p>
          </motion.div>
        </section>
      )}

      <footer className="mx-auto max-w-7xl border-t border-[#DBE9F6] px-4 py-8 sm:px-8">
        <p className="text-center text-xs text-[#94A3B8]">© 2026 KasPintar by EduNexa — Kelola keuangan sekolah dengan rapi dan transparan.</p>
        {adminContact?.phone && (
          <p className="mt-2 text-center text-xs text-[#94A3B8]" data-testid="login-admin-contact">
            Butuh bantuan / perpanjangan langganan? Hubungi admin: <b className="text-[#475569]">{adminContact.name}</b> · WA <b className="text-[#475569]">{adminContact.phone}</b>
          </p>
        )}
      </footer>

      <ContactPopup />
    </div>
  );
}
